import { c as _c } from "react/compiler-runtime";
import Checkbox from '@precis-digital/kurama/Checkbox';
import Grid from '@precis-digital/kurama/Grid';
import Skeleton from '@precis-digital/kurama/Skeleton';
import SortAscendingIcon from '@precis-digital/kurama/icons/SortAscendingIcon';
import SortDescendingIcon from '@precis-digital/kurama/icons/SortDescendingIcon';
import Table, { TableBody, TableBodyCell, TableContainer, TableHead, TableHeaderCell, TablePagination, TableRow } from '@precis-digital/kurama/Table';
import Tooltip from '@precis-digital/kurama/Tooltip';
import Typography from '@precis-digital/kurama/Typography';
import { styled, useTheme } from '@precis-digital/kurama/utils';
import { uniq } from 'ramda';
import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import type { ColumnInstance, HeaderGroup, Row, TableBodyPropGetter, TableBodyProps, TableHeaderProps, TablePropGetter, TableProps } from 'react-table';
import BlankIconSpacer from 'shared/components/BlankIconSpacer';
import { type TFunction } from 'shared/translations';
import States from './States';
export const DEFAULT_PAGE_SIZE = 20;
export const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];
type StylesList = 'container' | 'table';

// Base props shared by both pagination types
interface BasePaginationProps {
  page: number;
  handleChangePage: (event: unknown, newPage: number) => void;
  rowsPerPage: number;
  rowsPerPageOptions?: Array<number | {
    value: number;
    label: string;
  }>;
  shouldDisableNextButton?: boolean;
}

// CSR - Client Side Rendering (count ALWAYS required)
export interface PaginationCSR extends BasePaginationProps {
  isSSR: false;
  count: number; // REQUIRED for CSR
  onRowsPerPageChange?: (size: number) => void;
  // Either provide custom handler OR react-table functions (not both)
  handleChangeRowsPerPage?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setPageSize?: (size: number) => void;
  gotoPage?: (page: number) => void;
}

// SSR - Server Side Rendering (count optional, can be -1 for unknown)
export interface PaginationSSR extends BasePaginationProps {
  isSSR: true;
  count?: number; // Optional for SSR (can be undefined, defaults to -1)
  onRowsPerPageChange?: (size: number) => void;
  setPageSizeState: (size: number) => void;
  setPageState: (page: number) => void;
}
export type PaginationProps = PaginationCSR | PaginationSSR;
interface BaseTableProps<T extends object> {
  styles?: Partial<Record<StylesList, React.CSSProperties>>;
  getTableProps: (propGetter?: TablePropGetter<T>) => TableProps;
  getTableBodyProps: (propGetter?: TableBodyPropGetter<T>) => TableBodyProps;
  headerGroups: Array<HeaderGroup<T>>;
  rows: Array<Row<T>>;
  prepareRow: (row: Row<T>) => void;
  renderFilters?: () => React.ReactNode;
  pagination?: PaginationProps;
  footerGroups?: Array<HeaderGroup<T>>;
  isRowSelectable?: boolean;
  selectedRowIds?: string[];
  setSelectedRowIds?: (rowIds: string[]) => void;
  rowsSelectedText?: string;
  getRowId?: (row: Row<T>) => string;
  onRowClick?: (row: Row<T>) => void;
}
interface TablePropsWithoutSelection<T extends object> extends BaseTableProps<T> {
  isRowSelectable?: false;
  selectedRowIds?: never;
  setSelectedRowIds?: never;
  rowsSelectedText?: never;
  getRowId?: never;
}
interface TablePropsWithSelection<T extends object> extends BaseTableProps<T> {
  isRowSelectable?: true;
  selectedRowIds: string[];
  setSelectedRowIds: (rowIds: string[]) => void;
  getRowId: (row: Row<T>) => string;
  rowsSelectedText: string;
}
interface ReportTableProps<T extends object> {
  tableProps: TablePropsWithoutSelection<T> | TablePropsWithSelection<T>;
  style?: React.CSSProperties;
  t: TFunction<'home' | 'budgetOptimiserV2' | 'enhancedSignals' | 'channelGrouping' | 'connector' | 'recommendation' | 'attributionModelV2' | 'creativeReporting'>;
  basic?: boolean;
  isLoading?: boolean;
  // list of columns that are "basic" and should be aligned to the left, these are dimensional fields
  basicColumnsIds: string[];
  stickyColumnsIds?: string[];
  /** When set, only this sticky column shows the dimension/metrics vertical divider. */
  stickyDividerAfterColumnId?: string;
  /** When set, the Total footer label renders in this column (right-aligned). */
  totalFooterColumnId?: string;
  wrapWidthColumnsMap?: Record<string, string>;
  descriptionColumnsMap?: Record<string, string>;
}
interface ExtendedCellStylesProps {
  alignRight: boolean;
  columnLeftOffset: number;
  isStickyColumn: boolean;
  showStickyDivider: boolean;
  wrapWidth?: string;
}
interface SortableHeaderCellProps {
  column: ColumnInstance<any>;
  alignRight: boolean;
  description?: string;
}
const SortableHeaderText: React.FC<SortableHeaderCellProps> = t0 => {
  const $ = _c(18);
  const {
    column,
    alignRight,
    description
  } = t0;
  let t1;
  if ($[0] !== column.isSorted || $[1] !== column.isSortedDesc) {
    t1 = column.isSorted ? column.isSortedDesc === true ? <SortDescendingIcon /> : <SortAscendingIcon /> : <BlankIconSpacer width={16} height={24} />;
    $[0] = column.isSorted;
    $[1] = column.isSortedDesc;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const sortArrow = t1;
  let t2;
  if ($[3] !== column || $[4] !== description) {
    t2 = description != null ? <Tooltip title={description} kind="singleline"><Typography variant="h5">{column.render("Header")}</Typography></Tooltip> : <Typography variant="h5">{column.render("Header")}</Typography>;
    $[3] = column;
    $[4] = description;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const content = t2;
  if (alignRight) {
    let t3;
    if ($[6] !== column.isSorted || $[7] !== sortArrow) {
      t3 = <StyledSortIcon $isSorted={column.isSorted}>{sortArrow}</StyledSortIcon>;
      $[6] = column.isSorted;
      $[7] = sortArrow;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== content || $[10] !== t3) {
      t4 = <Grid display="flex" alignItems="center" flexWrap="nowrap" justifyContent="flex-end" gap={1}>{t3}{content}</Grid>;
      $[9] = content;
      $[10] = t3;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    return t4;
  }
  let t3;
  if ($[12] !== column.isSorted || $[13] !== sortArrow) {
    t3 = <StyledSortIcon $isSorted={column.isSorted}>{sortArrow}</StyledSortIcon>;
    $[12] = column.isSorted;
    $[13] = sortArrow;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== content || $[16] !== t3) {
    t4 = <Grid display="flex" alignItems="center" flexWrap="nowrap" justifyContent="flex-start" gap={1}>{content}{t3}</Grid>;
    $[15] = content;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
};
export const ReportTable = <T extends object,>({
  tableProps: {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    renderFilters,
    footerGroups = [],
    styles,
    pagination,
    isRowSelectable = false,
    selectedRowIds,
    setSelectedRowIds,
    rowsSelectedText,
    getRowId,
    onRowClick
  },
  style,
  basic = true,
  isLoading = false,
  t,
  basicColumnsIds,
  stickyColumnsIds = [],
  stickyDividerAfterColumnId,
  totalFooterColumnId,
  wrapWidthColumnsMap = {},
  descriptionColumnsMap = {}
}: ReportTableProps<T>): React.ReactElement => {
  const theme = useTheme();
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const arrayOfRefs = useRef<Array<React.RefObject<HTMLTableCellElement | null>>>([]);
  const refsToAdd = stickyColumnsIds.length - arrayOfRefs.current.length;
  if (refsToAdd > 0) {
    arrayOfRefs.current.push(...Array.from({
      length: refsToAdd
    }, () => React.createRef<HTMLTableCellElement | null>()));
  }
  const normalizedPagination = pagination == null ? undefined : {
    ...pagination,
    count: pagination.isSSR && pagination.count == null ? -1 : pagination.count ?? 0
  };
  const selectedTableRowIds = selectedRowIds ?? [];
  const currentPage = normalizedPagination?.page ?? 0;
  const selectedRowsOnPage = rows.filter(row => selectedTableRowIds.includes(getRowId?.(row) ?? ''));
  const totalSelectedRows = selectedTableRowIds?.length ?? 0;
  const isServerSidePagination = normalizedPagination?.isSSR === true;
  useEffect(() => {
    const observers: ResizeObserver[] = [];
    arrayOfRefs.current.forEach((ref, index) => {
      if (index >= stickyColumnsIds.length) return;
      if (ref?.current != null) {
        const resizeObserver = new ResizeObserver(() => {
          setColumnWidths(prevState => ({
            ...prevState,
            [index]: ref.current?.clientWidth
          }));
        });
        resizeObserver.observe(ref.current);
        observers.push(resizeObserver);
      }
    });
    return () => {
      observers.forEach(observer => {
        observer.disconnect();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...arrayOfRefs.current.map(c => c.current), headerGroups, stickyColumnsIds.length]);
  const getCellStylesFromColumn = (basicColumnsIds: string[], column: ColumnInstance<any>, index: number): ExtendedCellStylesProps => {
    const alignRight = !basicColumnsIds.includes(column.id);
    const isStickyColumn = stickyColumnsIds.includes(column.id);
    const showStickyDivider = isStickyColumn && (stickyDividerAfterColumnId == null || column.id === stickyDividerAfterColumnId);
    const columnLeftOffset = Array.from({
      length: index
    }, (_, i) => columnWidths[i] ?? 0).reduce((sum, w) => sum + w, 0);
    return {
      columnLeftOffset,
      isStickyColumn,
      showStickyDivider,
      alignRight
    };
  };
  const renderLoader = useCallback((): ReactNode => {
    if (normalizedPagination?.rowsPerPage != null && normalizedPagination?.count != null) {
      const hasFooter = footerGroups.length > 0;
      return Array.from({
        length: (normalizedPagination.count <= 0 ? normalizedPagination.rowsPerPage : Math.min(normalizedPagination.rowsPerPage, normalizedPagination.count)) + (hasFooter ? 1 : 0)
      }).map((_, index) => <TableRow key={index}>
          <TableBodyCell colSpan={headerGroups[0].headers.length + (headerGroups[1] != null ? headerGroups[1].headers.length : 0)} height={48}>
            <Skeleton height={40} />
          </TableBodyCell>
        </TableRow>);
    }
    return <TableRow>
        <TableBodyCell colSpan={headerGroups[0].headers.length + (headerGroups[1] != null ? headerGroups[1].headers.length : 0)}>
          <Skeleton height={273} />
        </TableBodyCell>
      </TableRow>;
  }, [normalizedPagination?.rowsPerPage, headerGroups, normalizedPagination?.count, footerGroups.length]);
  const handleRowSelect = (row: Row<T>, checked: boolean): void => {
    const filterFn = (selectedRowId: string): boolean => selectedRowId !== getRowId?.(row);
    setSelectedRowIds?.(checked ? uniq([...selectedTableRowIds, ...(getRowId != null ? [getRowId(row)] : [])]) : selectedTableRowIds.filter(filterFn));
  };
  const handleAllRowsSelect = (_: unknown, checked: boolean): void => {
    const getRowIdFromRow = (row: Row<T>): string => getRowId?.(row) ?? '';
    const isRowNotInCurrentPage = (selectedRowId: string): boolean => !rows.some(row => getRowId?.(row) === selectedRowId);
    const rowIds = checked ? uniq([...selectedTableRowIds, ...rows.map(getRowIdFromRow)]) : selectedTableRowIds.filter(isRowNotInCurrentPage);
    setSelectedRowIds?.(rowIds);
  };
  const getRowsPerPageOptions = (): Array<number | {
    value: number;
    label: string;
  }> => {
    if (normalizedPagination?.rowsPerPageOptions != null) {
      return normalizedPagination.rowsPerPageOptions;
    }
    if (normalizedPagination?.isSSR === true) {
      return DEFAULT_ROWS_PER_PAGE_OPTIONS;
    }
    return [...DEFAULT_ROWS_PER_PAGE_OPTIONS, {
      label: 'All',
      value: normalizedPagination?.count ?? 0
    }];
  };
  if (rows.length === 0 && !isLoading) {
    return <Grid container style={{
      marginTop: theme.spacing(3),
      gap: theme.spacing(4),
      ...style
    }}>
        {renderFilters?.()}
        <States variant="noMatchingResults" title={t('noDetailedReport')} />
      </Grid>;
  }
  return <Grid container style={{
    marginTop: '24px',
    ...style
  }}>
      {renderFilters?.()}
      <StyledTableContainer style={styles?.container}>
        <StyledTable {...getTableProps()} style={styles?.table}>
          <StyledTableHead>
            {headerGroups.map((headerGroup, headerGroupIndex) => {
            const {
              key,
              ...restHeaderGroup
            } = headerGroup.getHeaderGroupProps();
            return <StyledTableRow {...restHeaderGroup} key={key}>
                  {headerGroup.headers.map((column, columnIndex) => {
                const {
                  key
                } = column.getHeaderProps();
                const {
                  alignRight,
                  isStickyColumn,
                  showStickyDivider,
                  columnLeftOffset
                } = getCellStylesFromColumn(basicColumnsIds, column, columnIndex);
                if (column.parent !== undefined) {
                  return <StyledTableSubHeaderCell {...{
                    basic,
                    alignRight,
                    isStickyColumn,
                    showStickyDivider,
                    columnLeftOffset
                  }} {...column.getHeaderProps(column.getSortByToggleProps())} key={key} ref={arrayOfRefs.current[columnIndex]} width="100px">
                          <SortableHeaderText column={column} alignRight={alignRight} description={descriptionColumnsMap[column.id]} />
                        </StyledTableSubHeaderCell>;
                } else {
                  // This indicates if there are 2 headers and this one is the first(top) one, In that case
                  // it behaves more like a column group label...
                  const isParentHeader = headerGroups.length > 1 && headerGroupIndex === 0;
                  const alignRightParentHeader = alignRight && !isParentHeader;
                  const sortByToggleProps = isParentHeader ? {} : column.getSortByToggleProps();
                  const wrapWidth = wrapWidthColumnsMap[column.id];
                  return <StyledTableHeaderCell {...{
                    basic,
                    columnLeftOffset,
                    isStickyColumn,
                    showStickyDivider,
                    alignRight: alignRightParentHeader,
                    isParentHeader,
                    wrapWidth
                  }} {...column.getHeaderProps(sortByToggleProps)} key={key} ref={arrayOfRefs.current[columnIndex]} {...wrapWidth == null && {
                    width: column.width
                  }}>
                          {!isParentHeader && isRowSelectable && columnIndex === 0 ? <Grid display="flex" key={key} alignItems="center" gap={selectedRowsOnPage.length > 0 ? theme.spacing(6) : theme.spacing(4)}>
                              <Grid onClick={e => {
                        e.stopPropagation();
                      }} display="flex" alignItems="center" justifyContent="center" gap="16px">
                                <StyledCheckbox checked={currentPage != null && selectedRowsOnPage.length === rows.length} indeterminate={currentPage != null && selectedRowsOnPage.length > 0 && selectedRowsOnPage.length < rows.length} onChange={handleAllRowsSelect} />

                                {totalSelectedRows > 0 && <Typography variant="h5">{rowsSelectedText}</Typography>}
                              </Grid>

                              <SortableHeaderText column={column} alignRight={alignRightParentHeader} description={descriptionColumnsMap[column.id]} />
                            </Grid> : <SortableHeaderText column={column} alignRight={alignRightParentHeader} description={descriptionColumnsMap[column.id]} />}
                        </StyledTableHeaderCell>;
                }
              })}
                </StyledTableRow>;
          })}
          </StyledTableHead>

          {isLoading ? renderLoader() : rows.length > 0 && <TableBody {...getTableBodyProps()}>
                  {rows.map(row => {
            prepareRow(row);
            const {
              key: rowKey,
              ...rowProps
            } = row.getRowProps();
            return <StyledTableBodyRow {...rowProps} key={rowKey} onClick={() => {
              onRowClick?.(row);
            }} style={onRowClick == null ? undefined : {
              cursor: 'pointer'
            }}>
                        {row.cells.map((cell, columnIndex) => {
                const cellOrAggregatedComponent = cell.isAggregated ? cell.render('Aggregated') : cell.render('Cell');
                const {
                  alignRight,
                  isStickyColumn,
                  showStickyDivider,
                  columnLeftOffset
                } = getCellStylesFromColumn(basicColumnsIds, cell.column, columnIndex);
                const wrapWidth = wrapWidthColumnsMap[cell.column.id];
                return <StyledTableBodyCell {...{
                  basic,
                  isStickyColumn,
                  showStickyDivider,
                  columnLeftOffset,
                  alignRight,
                  wrapWidth
                }} {...cell.getCellProps()} key={cell.column.id} {...wrapWidth == null && {
                  width: cell.column.width
                }}>
                              {isRowSelectable && columnIndex === 0 ? <Grid display="flex" gap="8px">
                                  <Checkbox checked={selectedRowsOnPage.some(selectedRow => getRowId?.(selectedRow) === getRowId?.(row))} onChange={(_, checked) => {
                      handleRowSelect(row, checked);
                    }} />
                                  {cellOrAggregatedComponent}
                                </Grid> : cellOrAggregatedComponent}
                            </StyledTableBodyCell>;
              })}
                      </StyledTableBodyRow>;
          })}
                  <>
                    {footerGroups.map((group, footerGroupIndex) => {
              const {
                key
              } = group.getFooterGroupProps();
              return <StyledFooterRow {...group.getFooterGroupProps()} key={key}>
                          {group.headers.map((column, columnIdx) => {
                  if (footerGroupIndex !== 0) return null;
                  const {
                    alignRight,
                    isStickyColumn,
                    showStickyDivider,
                    columnLeftOffset
                  } = getCellStylesFromColumn(basicColumnsIds, column, columnIdx);
                  const {
                    key
                  } = column.getFooterProps();
                  const isTotalFooterCell = totalFooterColumnId == null ? columnIdx === 0 : column.id === totalFooterColumnId;
                  if (isTotalFooterCell) {
                    return <StyledTableBodyCell {...{
                      basic,
                      isStickyColumn,
                      showStickyDivider,
                      alignRight: totalFooterColumnId == null ? alignRight : true,
                      columnLeftOffset,
                      isSelectable: isRowSelectable
                    }} {...column.getFooterProps()} key={key}>
                                  <Typography variant="h4">{t('total')}</Typography>
                                </StyledTableBodyCell>;
                  }
                  return <StyledTableBodyCell {...{
                    basic,
                    isStickyColumn,
                    showStickyDivider,
                    alignRight,
                    columnLeftOffset
                  }} {...column.getFooterProps()} key={key}>
                                <Typography variant="h4">{column.render('Footer')}</Typography>
                              </StyledTableBodyCell>;
                })}
                        </StyledFooterRow>;
            })}
                  </>
                </TableBody>}
        </StyledTable>
      </StyledTableContainer>
      {normalizedPagination != null && <TablePagination count={normalizedPagination.count} page={normalizedPagination.page} onPageChange={normalizedPagination.handleChangePage} rowsPerPage={normalizedPagination.rowsPerPage} rowsPerPageOptions={getRowsPerPageOptions()} shouldDisableNextButton={normalizedPagination.shouldDisableNextButton} onRowsPerPageChange={(!normalizedPagination.isSSR || normalizedPagination.isSSR == null) && 'handleChangeRowsPerPage' in normalizedPagination && normalizedPagination.handleChangeRowsPerPage != null ? normalizedPagination.handleChangeRowsPerPage : (event: React.ChangeEvent<HTMLInputElement>) => {
      const size = Number.parseInt(event.target.value, 10);
      if (normalizedPagination.isSSR) {
        // SSR pagination - calculate new page to maintain position
        const currentFirstRowIndex = normalizedPagination.page * normalizedPagination.rowsPerPage;
        const newPage = Math.floor(currentFirstRowIndex / size);
        // Update page size state first
        normalizedPagination.setPageSizeState(size);
        // Update page state
        normalizedPagination.setPageState(newPage);
      } else if (normalizedPagination.setPageSize != null && normalizedPagination.gotoPage != null) {
        // CSR pagination - react-table's setPageSize
        normalizedPagination.setPageSize(size);
        // Note: setPageSize should handle page index, but keeping gotoPage as fallback
      }
      normalizedPagination.onRowsPerPageChange?.(size);
    }} labelDisplayedRows={({
      from,
      to
    }) => {
      const count = normalizedPagination?.count ?? 0;
      const showMany = isServerSidePagination && count <= 0;
      return `${from}–${to} of ${showMany ? 'many' : count}`;
    }} />}
    </Grid>;
};
interface ConditionalStylesProps {
  basic: boolean;
  isStickyColumn: boolean;
  isParentHeader?: boolean;
  isSelectable?: boolean;
}
type StyledTableBodyCellProps = TableBodyProps & ConditionalStylesProps & ExtendedCellStylesProps;
type StyledTableHeaderCellProps = TableHeaderProps & ConditionalStylesProps & ExtendedCellStylesProps;
type StyledTableSubHeaderCellProps = StyledTableHeaderCellProps & ExtendedCellStylesProps;
interface StyledTableProps {
  $margin?: string;
}
const StyledTable = styled(Table)<StyledTableProps>(({
  $margin: margin
}) => ({
  margin: margin ?? '24px 0',
  borderCollapse: 'collapse'
}));
const StyledTableSubHeaderCell = styled(TableHeaderCell, {
  shouldForwardProp: prop => prop !== 'isStickyColumn' && prop !== 'showStickyDivider' && prop !== 'columnLeftOffset' && prop !== 'wrapWidth' && prop !== 'isSelectable'
})<StyledTableSubHeaderCellProps>(({
  theme,
  alignRight,
  isStickyColumn,
  columnLeftOffset
}) => ({
  textAlign: alignRight ? 'right' : 'start',
  textWrap: 'nowrap',
  padding: '12px 0px 12px 16px',
  backgroundColor: theme.tokens.background.main.default,
  verticalAlign: 'top',
  ...(isStickyColumn && {
    position: 'sticky',
    left: columnLeftOffset,
    zIndex: 4
  })
}));
const StyledTableHeaderCell = styled(TableHeaderCell, {
  shouldForwardProp: prop => prop !== 'basic' && prop !== 'isParentHeader' && prop !== 'wrapWidth' && prop !== 'alignRight' && prop !== 'isSelectable' && prop !== 'columnLeftOffset' && prop !== 'isStickyColumn' && prop !== 'showStickyDivider'
})<StyledTableHeaderCellProps>(({
  theme,
  alignRight,
  columnLeftOffset,
  isStickyColumn,
  showStickyDivider
}) => ({
  textWrap: 'nowrap',
  paddingLeft: '16px',
  textAlign: alignRight ? 'right' : 'start',
  borderImageWidth: 0,
  verticalAlign: 'top',
  ...(isStickyColumn && {
    position: 'sticky',
    left: columnLeftOffset,
    zIndex: 4,
    paddingRight: showStickyDivider ? theme.spacing(1) : '2px',
    backgroundColor: theme.tokens.background.main.default
  }),
  '&::after': showStickyDivider ? {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '2px',
    height: '100%',
    background: theme.tokens.background.tonal.soft.default,
    pointerEvents: 'none',
    zIndex: 3
  } : {},
  '&:first-of-type': {
    paddingLeft: '0px'
  }
}));
const StyledTableRow = styled(TableRow)(({
  theme
}) => ({
  borderRadius: '32px',
  height: '32px'
}));
const StyledFooterRow = styled(TableRow)(() => ({
  marginTop: '16px',
  height: '52px',
  position: 'sticky',
  bottom: 0,
  zIndex: 2
}));
const StyledTableBodyCell = styled(TableBodyCell, {
  shouldForwardProp: prop => prop !== 'columnLeftOffset' && prop !== 'alignRight' && prop !== 'basic' && prop !== 'isStickyColumn' && prop !== 'showStickyDivider' && prop !== 'isSelectable' && prop !== 'wrapWidth'
})<StyledTableBodyCellProps>(({
  theme,
  isStickyColumn,
  showStickyDivider,
  columnLeftOffset,
  alignRight,
  wrapWidth
}) => ({
  textWrap: wrapWidth != null ? 'normal' : 'nowrap',
  paddingLeft: '16px',
  textAlign: alignRight ? 'right' : 'start',
  backgroundColor: theme.tokens.background.main.default,
  borderImageWidth: 0,
  ...(isStickyColumn && {
    position: 'sticky',
    left: columnLeftOffset,
    paddingRight: showStickyDivider ? theme.spacing(1) : '2px',
    zIndex: 2
  }),
  '&::after': showStickyDivider ? {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '2px',
    height: '100%',
    background: theme.tokens.background.tonal.soft.default,
    pointerEvents: 'none',
    zIndex: 1
  } : {},
  ...(wrapWidth != null && {
    lineHeight: 1.2,
    maxWidth: wrapWidth,
    minWidth: wrapWidth,
    width: wrapWidth,
    overflow: 'hidden',
    whiteSpace: 'normal'
  }),
  '&:first-of-type': {
    paddingLeft: '0px'
  }
}));
const StyledTableBodyRow = styled(TableRow)(({
  theme
}) => ({
  height: '48px',
  borderBottom: `1px solid ${theme.tokens.background.tonal.soft.default}`,
  ':not(:nth-last-of-type(2))': {
    borderBottom: `1px solid ${theme.tokens.background.tonal.soft.default}`
  }
}));
const StyledTableHead = styled(TableHead)(({
  theme
}) => ({
  position: 'sticky',
  top: 0,
  zIndex: 3,
  backgroundColor: theme.tokens.background.main.default
}));
const StyledTableContainer = styled(TableContainer)(() => ({
  marginTop: 20,
  overflow: 'auto',
  maxHeight: 800,
  minHeight: 400,
  padding: '0 15px 0 0'
}));
const StyledSortIcon = styled('span')<{
  $isSorted: boolean;
}>(({
  theme,
  $isSorted: isSorted
}) => ({
  color: isSorted ? theme.tokens.foreground.main.link : theme.tokens.foreground.main.secondary,
  display: 'inline-flex',
  alignItems: 'center'
}));
const StyledCheckbox = styled(Checkbox)(() => ({
  paddingTop: '0px',
  paddingBottom: '0px',
  paddingRight: '0px'
}));
export default ReportTable;